import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { C } from "@/brand/tokens";
import {
  pathways5,
  pathwayDetails,
  pathwaySlugs,
} from "@/brand/content";
import { SiteHeader5 } from "@/components/layout/SiteHeader5";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { InteriorHero } from "@/components/common/InteriorHero";
import { ConsultationBanner } from "@/components/common/ConsultationBanner";

/**
 * PathwayPage — dynamic template for the six pathway detail pages.
 *
 * Route: /concept-e/pathways/:slug
 */
export default function PathwayPage() {
  const { slug } = useParams();
  const n = pathwaySlugs[slug];
  if (!n) return <Navigate to="/concept-e" replace />;

  const pathway = pathways5.find((p) => p.n === n);
  const detail = pathwayDetails[n];

  // Related pathways — the other three
  const related = pathways5
    .filter((p) => p.n !== n)
    .slice(0, 3);

  return (
    <main
      data-testid="page-pathway"
      className="min-h-screen"
      style={{
        background: C.surfaceLight,
        color: C.textOnLight,
        fontFamily: "Inter, sans-serif",
      }}
    >
      <SiteHeader5 />

      <InteriorHero
        breadcrumb={[
          { label: "Faith 1st", href: "/concept-e" },
          { label: "Services", href: "/concept-e#pathways" },
          { label: pathway.label },
        ]}
        eyebrow={`Service ${pathway.n} of 04`}
        title={pathway.label}
        intro={pathway.descriptor}
        meta={[
          ["Best when", pathway.bestWhen],
          ["Length", detail.engagementLength],
        ]}
      />

      {/* Engagement shape */}
      <section
        data-testid="pathway-shape"
        style={{ background: C.surfaceLight2 }}
      >
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-4 mb-7">
                <span
                  aria-hidden="true"
                  className="inline-block h-px w-14"
                  style={{ background: C.accentMetal }}
                />
                <p
                  className="font-inter text-[11px] uppercase font-semibold"
                  style={{ color: C.accentMetal, letterSpacing: "0.28em" }}
                >
                  Engagement shape
                </p>
              </div>
              <h2
                className="font-cormorant"
                style={{
                  color: C.textOnLight,
                  fontSize: "clamp(1.8rem, 2.8vw, 2.4rem)",
                  fontWeight: 400,
                  lineHeight: 1.08,
                  letterSpacing: "-0.008em",
                }}
              >
                What the work
                <br />
                looks like in practice.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <ul>
                {detail.engagementShape.map((item, i) => (
                  <li
                    key={item}
                    data-testid={`shape-${i}`}
                    className="py-6 grid grid-cols-12 gap-4"
                    style={{
                      borderTop:
                        i === 0 ? `1px solid ${C.borderSoft}` : "none",
                      borderBottom: `1px solid ${C.borderSoft}`,
                    }}
                  >
                    <span
                      className="col-span-2 font-cormorant tabular-nums"
                      style={{
                        color: C.accentMetal,
                        fontSize: "1.6rem",
                        lineHeight: 1,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p
                      className="col-span-10 font-inter leading-[1.65]"
                      style={{ color: C.inkOnLight, fontSize: "1rem" }}
                    >
                      {item}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Who + outcome */}
      <section
        data-testid="pathway-who-outcome"
        style={{
          background: C.surfaceLight,
          borderTop: `1px solid ${C.borderSoft}`,
        }}
      >
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-20 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
            <article className="lg:col-span-6">
              <p
                className="font-inter text-[11px] uppercase font-semibold pb-3 mb-6"
                style={{
                  color: C.accentMetal,
                  letterSpacing: "0.28em",
                  borderBottom: `1px solid ${C.borderSoft}`,
                }}
              >
                Who reaches out for this
              </p>
              <p
                className="font-inter leading-[1.75]"
                style={{
                  color: C.inkOnLight,
                  fontSize: "clamp(1.02rem, 1.1vw, 1.1rem)",
                }}
              >
                {detail.whoItsFor}
              </p>
            </article>
            <article className="lg:col-span-6">
              <p
                className="font-inter text-[11px] uppercase font-semibold pb-3 mb-6"
                style={{
                  color: C.accentMetal,
                  letterSpacing: "0.28em",
                  borderBottom: `1px solid ${C.borderSoft}`,
                }}
              >
                What changes as a result
              </p>
              <p
                className="font-cormorant italic"
                style={{
                  color: C.textOnLight,
                  fontSize: "clamp(1.15rem, 1.4vw, 1.35rem)",
                  lineHeight: 1.5,
                }}
              >
                {detail.outcome}
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* Related pathways */}
      <section
        data-testid="pathway-related"
        style={{
          background: C.surfaceLight,
          borderTop: `1px solid ${C.borderSoft}`,
        }}
      >
        <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-20 lg:py-24">
          <div className="flex items-baseline justify-between gap-4 mb-10">
            <div className="flex items-center gap-4">
              <span
                aria-hidden="true"
                className="inline-block h-px w-14"
                style={{ background: C.accentMetal }}
              />
              <p
                className="font-inter text-[11px] uppercase font-semibold"
                style={{ color: C.accentMetal, letterSpacing: "0.28em" }}
              >
                Adjacent services
              </p>
            </div>
            <Link
              to="/concept-e#pathways"
              data-testid="all-pathways-link"
              className="group inline-flex items-center gap-2 pb-1 font-inter text-[10.5px] uppercase font-semibold border-b transition-colors"
              style={{
                color: C.accentMetal,
                borderColor: C.accentMetal,
                letterSpacing: "0.24em",
              }}
            >
              View all four
              <ArrowRight
                size={11}
                weight="bold"
                className="group-hover:translate-x-1 transition-transform"
              />
            </Link>
          </div>
          <ul
            className="grid md:grid-cols-3 gap-px"
            style={{ background: C.borderSoft }}
          >
            {related.map((p) => {
              const rDetail = pathwayDetails[p.n];
              return (
                <li key={p.label} style={{ background: C.surfaceLight }}>
                  <Link
                    to={`/concept-e/pathways/${rDetail.slug}`}
                    className="group block p-8 h-full transition-colors"
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = C.surfaceLight2)
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = C.surfaceLight)
                    }
                  >
                    <div className="flex items-baseline justify-between gap-3 mb-4">
                      <span
                        className="font-cormorant tabular-nums"
                        style={{
                          color: C.accentMetal,
                          fontSize: "1.4rem",
                          lineHeight: 1,
                        }}
                      >
                        {p.n}
                      </span>
                      <ArrowUpRight
                        size={18}
                        weight="thin"
                        style={{ color: C.accentMetal }}
                        className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                      />
                    </div>
                    <h3
                      className="font-cormorant mb-3"
                      style={{
                        color: C.textOnLight,
                        fontSize: "1.25rem",
                        lineHeight: 1.2,
                      }}
                    >
                      {p.label}
                    </h3>
                    <p
                      className="font-inter text-[13px] leading-[1.6]"
                      style={{ color: C.inkOnLightSoft }}
                    >
                      {p.descriptor.slice(0, 100)}…
                    </p>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <ConsultationBanner
        title={`Discuss ${pathway.label}.`}
        body={`If ${pathway.label} looks like the moment your organization is in, we'd welcome the conversation. Katrina reads every inquiry personally.`}
        ctaLabel="Book a consultation"
      />
      <SiteFooter
        stamp="Concept 05"
        labelSuffix={`Service · ${pathway.n}`}
        markSubtitle="Leadership, People & Culture"
        descriptor="Faith 1st is a Leadership, People & Culture Consulting firm helping mission-driven and values-led organizations navigate high-stakes people moments through consulting, training, coaching, and facilitation."
      />
    </main>
  );
}
